import React from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Collapse from '@mui/material/Collapse';
import Pagination from '@mui/material/Pagination';
import PropTypes from 'prop-types';
import Loader from '@/components/common/Loader';
import SortBy from '@/constants/contributions/DonationSortBy';
import ContributionType from '@/constants/ContributionType';
import useDonationsSearch from '@/lib/hooks/swr/useDonationsSearch';
import translate from '@/lib/intl/translate';
import OutstandingPledgesTable from './OutstandingPledgesTable';

const PAGE_SIZE = 5;
const SORT = SortBy.PLEDGE_DUE_DATE;
const CLASSES = {
  pagination: {
    root: 'flex justify-center',
  },
};

export default function OutstandingPledgesContainer({
  constituentId,
  onViewClick,
}) {
  const [hasPledges, setHasPledges] = React.useState(false);
  const [totalHits, setTotalHits] = React.useState(0);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const handleOnPageChange = (e, newPage) => {
    setPage(newPage);
  };

  const donationSearchParams = React.useMemo(() => {
    return {
      constituentIds: [constituentId],
      donationTypes: [ContributionType.PLEDGE],
      pageFrom: page ? (page - 1) * PAGE_SIZE : 0,
      pageSize: PAGE_SIZE,
      sortBy: SORT,
    };
  }, [constituentId, page]);

  const {
    data: pledges,
    isLagging,
    isValidating,
  } = useDonationsSearch(donationSearchParams);

  React.useEffect(() => {
    if (pledges && !totalHits && !hasPledges) {
      setTotalHits(pledges.totalHits);
      setHasPledges(!!pledges.donations.length);
    }
  }, [pledges]);

  const handleViewClick = (donationId) => {
    onViewClick(
      pledges.donations.find((pledge) => pledge.donationId === donationId),
    );
  };

  return (
    <Collapse in={hasPledges} mountOnEnter>
      <Accordion
        className="rounded-lg bg-grey-100 shadow-none"
        expanded={isExpanded}
        data-testid="outstanding-pledges"
      >
        <AccordionSummary
          data-testid="outstanding-pledges-toggle-button"
          expandIcon={<ArrowDropDownIcon />}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <h3 className="type-h3">
            {translate('DONATION_OUTSTANDING_PLEDGES', {
              totalHits,
            })}
          </h3>
        </AccordionSummary>
        <AccordionDetails>
          <div className="flex w-full flex-col">
            {isValidating && !pledges?.donations ? (
              <Loader isCentered className="my-4" />
            ) : (
              <OutstandingPledgesTable
                donations={pledges?.donations || []}
                isLagging={isLagging}
                totalHits={totalHits}
                onViewClick={handleViewClick}
              />
            )}
            {totalHits > PAGE_SIZE && (
              <Pagination
                classes={CLASSES.pagination}
                count={Math.ceil((totalHits || 0) / PAGE_SIZE)}
                data-testid="outstanding-pledges-pagination"
                page={page}
                onChange={handleOnPageChange}
                shape="rounded"
              />
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </Collapse>
  );
}

OutstandingPledgesContainer.propTypes = {
  constituentId: PropTypes.string.isRequired,
  onViewClick: PropTypes.func.isRequired,
};
